import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import duration from "dayjs/plugin/duration";
import isBetween from "dayjs/plugin/isBetween";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { TIMEZONE_MAP, pluralize } from "@web-app/utils/helpers";
import http from "@web-app/utils/http";

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(advancedFormat);
dayjs.extend(relativeTime);

export { dayjs };

export const convertTimeAsTimezone = (date: Date, timezone: string): Date => {
  if (!timezone || timezone === "system") return date;

  const timezoneToUse = TIMEZONE_MAP[timezone] || timezone;

  // Convert the Date to the target timezone and then to UTC
  const dayjsInTimezone = dayjs(date).tz(timezoneToUse);

  return dayjsInTimezone.utc().toDate();
};

/**
 * Converts appFilter scheduled timestamps from user timezone to UTC for backend queries
 * @param appFilter - AppFilter with scheduled date times in user timezone
 * @param userTimezone - User's timezone (IANA timezone name)
 * @returns AppFilter with scheduled date times converted to UTC
 */
export const convertAppFilterToUTC = <
  T extends { scheduledDateTimeEnd?: string; scheduledDateTimeStart?: string },
>(
  appFilter: T,
  userTimezone: string,
): T => {
  if (!userTimezone || userTimezone === "system") return appFilter;

  const timezoneToUse = TIMEZONE_MAP[userTimezone] || userTimezone;

  const convertedFilter = { ...appFilter };

  if (appFilter.scheduledDateTimeStart) {
    const userTzDate = dayjs.tz(
      appFilter.scheduledDateTimeStart,
      timezoneToUse,
    );
    convertedFilter.scheduledDateTimeStart = userTzDate.utc().toISOString();
  }

  if (appFilter.scheduledDateTimeEnd) {
    const userTzDate = dayjs.tz(appFilter.scheduledDateTimeEnd, timezoneToUse);
    convertedFilter.scheduledDateTimeEnd = userTzDate.utc().toISOString();
  }

  return convertedFilter;
};

export const isSameDate = (date1: Date, date2: Date): boolean => {
  const date1Local = new Date(date1);
  const date2Local = new Date(date2);

  const formatedDate1 = date1Local.toLocaleDateString("en-GB");
  const formatedDate2 = date2Local.toLocaleDateString("en-GB");

  return formatedDate1 === formatedDate2;
};

export const getTimezoneFromAddress = async (address: {
  lat: number;
  lng: number;
}) => {
  return http
    .post(`/timezone/fromAddress`, address)
    .then((response) => response.data);
};

export const getUserReadableTimestringFromSeconds = (
  seconds: number,
): string => {
  const dur = dayjs.duration(seconds, "seconds");
  const timeUnits = [
    { label: pluralize("month", dur.months()), value: dur.months() },
    { label: pluralize("day", dur.days()), value: dur.days() },
    { label: pluralize("hour", dur.hours()), value: dur.hours() },
    { label: pluralize("minute", dur.minutes()), value: dur.minutes() },
    { label: pluralize("second", dur.seconds()), value: dur.seconds() },
  ];
  return timeUnits
    .filter((unit) => unit.value > 0)
    .map((unit) => `${unit.value} ${unit.label}`)
    .join(" ");
};

/**
 * The browser's IANA timezone (e.g. "America/New_York"), so the server can read
 * dates the way the user means them instead of in UTC.
 */
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}
